<template>
  <div class="d-flex align-items-center flex-wrap">
    <slot :title="title">
      <a href="#" @click.prevent="showModal = true" class="btn btn-sm ms-0 ps-0" :class="linkClass">
        {{title}}
      </a>
    </slot>

    <!-- Modal with Vue-controlled visibility -->
    <div class="modal" :class="{ 'fade': true, 'show': showModal }" 
         :style="{ display: showModal ? 'block' : 'none' }" 
         tabindex="-1" 
         aria-labelledby="dataEditorModalLabel" 
         :aria-hidden="!showModal"
         @click.self="closeModal">
      
      <!-- Modal backdrop -->
      <div v-if="showModal" class="modal-backdrop fade show" @click="closeModal"></div>
      
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="dataEditorModalLabel">
              {{title}} 
              <div v-if="!isCustomDate" class="d-inline fs-sm fw-light">({{rangeSummary}})</div>
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          
          <div class="modal-body overflow-auto">
            <div class="row calendar-override">
              <!-- Left Column: Period Type Lists -->
              <div class="col-lg-4">
                <!-- Custom Ranges Section -->
                <div class="mb-4">
                  <div class="fw-bold d-flex align-items-end justify-content-between">
                    Custom Ranges
                    <VcTooltip title="Custom date or date ranges" content="Custom date or date ranges" placement="bottom"
                      trigger="hover">
                      <a href="#" class="ms-1 text-muted opacity-50">

                        <i class="bi bi-info-circle"></i>
                      </a>
                    </VcTooltip>
                  </div>

                  <ul class="list-group">
                    <li v-for="(u,i) in customPeriodTypes" :key="i" 
                        class="list-group-item d-flex align-items-center p-1"  
                        :class="{'active':selectedPeriodType===u, 'disabled':isEditing && !u.edit}">
                      <button class="btn btn-sm text-primary" 
                              :class="{'text-light':selectedPeriodType===u}" 
                              @click="setStartPeriodType(u)">
                        {{ getPeriodTypeLabel(u, true) }}
                      </button>
                      <button v-if="u.pattern && selectedPeriodType===u" 
                              class="btn btn-sm ms-auto" 
                              :class="{'text-light':selectedPeriodType===u}" 
                              @click="u.edit=!u.edit" 
                              :disabled="paramError">
                        <i class="bi bi-pencil"></i>
                      </button>
                    </li>
                  </ul>
                </div>

                <!-- Dynamic Dates Section -->
                <div>
                  <div class="fw-bold d-flex align-items-end justify-content-between">
                    Dynamic Dates
                    <VcTooltip title="Date ranges that change relative to today" content="Date ranges that change relative to today" placement="bottom"
                      trigger="hover">
                      <a href="#" class="ms-1 text-muted opacity-50">

                        <i class="bi bi-info-circle"></i>
                      </a>
                    </VcTooltip>
                  </div>
                  <div class="d-block d-lg-none">
                    
                        <button v-if="isCustomDate || !selectedPeriodType" class="btn btn-sm btn-outline-primary" @click="setDefaultDynamicStartPeriodType()">
                          <i class="bi bi-plus me-1"></i>Add dynamic start
                        </button>
                        <!-- Edit Mode for End Period -->
                        <div v-else-if="selectedPeriodType.edit" class="d-flex flex-column">
                          <div class="d-flex align-items-center">
                            <template v-for="(v,i) in selectedPeriodType.pieces" :key="i">
                              <div v-if="v==='static'">{{ selectedPeriodType.params[i] }}</div>
                              <input v-else-if="v==='num'" 
                                     type="number" 
                                     class="form-control mx-2 px-1"  
                                     v-model.number="selectedPeriodType.params[i]" 
                                     @change="setStartPeriodType(selectedPeriodType)"
                                     @keyup.enter="selectedPeriodType.edit = false">
                              <input v-else-if="v==='str'" 
                                     type="text" 
                                     class="form-control mx-2"  
                                     v-model="selectedPeriodType.params[i]"  
                                     @change="setStartPeriodType(selectedPeriodType)"
                                      @keyup.enter="selectedPeriodType.edit = false">
                            </template>
                          </div>
                          <div v-if="paramError" class="fs-sm text-danger">{{ paramError }}</div>
                        </div>

                        <!-- Normal End Period Display with Vue-controlled dropdown -->
                        <div v-else class="d-flex align-items-center gap-2">
                          <div class="dropdown" ref="startPeriodDropdown">
                            <button class="btn btn-sm btn-outline-primary dropdown-toggle" 
                                    type="button"
                                    @click="toggleStartPeriodDropdown"
                                    :aria-expanded="selectedPeriodType">
                              {{ getPeriodTypeLabel(selectedPeriodType, true) }}
                            </button>

                            <ul class="dropdown-menu" :class="{ show: showStartPeriodDropdown }">
                              <li v-for="(u,i) in dynamicPeriodTypes" :key="i" >
                                <a class="dropdown-item" 
                                   href="#" 
                                   @click.prevent="selectStartPeriodType(u)">
                                  {{ getPeriodTypeLabel(u, true) }}
                                </a>
                              </li>
                            </ul>
                          </div>
                          
                          <button v-if="selectedPeriodType.pattern" 
                                  class="btn btn-sm"  
                                  @click="selectedPeriodType.edit = true" 
                                  :disabled="paramError">
                            <i class="bi bi-pencil"></i>
                          </button>
        
                        </div>
                      

                  </div>

                  <ul class="d-none d-lg-block list-group overflow-auto" style="max-height: 300px;">
                    <li v-for="(u,i) in dynamicPeriodTypes" :key="i" 
                        class="list-group-item d-flex align-items-center p-1 fs-sm"  
                        :class="{'active':selectedPeriodType===u, 'disabled':isEditing && !u.edit}">
                      
                      <!-- Edit Mode -->
                      <div v-if="u.edit" class="d-flex flex-column w-100">
                        <div class="d-flex align-items-center">
                          <template v-for="(v,i) in u.pieces" :key="i">
                            <div v-if="v==='static'">{{ u.params[i] }}</div>
                            <input v-else-if="v==='num'" 
                                   type="number" 
                                   class="form-control mx-2 px-1"  
                                   v-model.number="u.params[i]" 
                                   @change="setStartPeriodType(u)"
                                   @keyup.enter="u.edit = false">
                            <input v-else-if="v==='str'" 
                                   type="text" 
                                   class="form-control mx-2"  
                                   v-model="u.params[i]"  
                                   @change="setStartPeriodType(u)"
                                   @keyup.enter="u.edit = false">
                          </template>
                        </div>
                        <div v-if="paramError" class="fs-sm text-danger">{{ paramError }}</div>
                      </div>

                      <!-- Normal Mode -->
                      <template v-else>
                        <button class="btn btn-sm text-primary" 
                                :class="{'text-light':selectedPeriodType===u}" 
                                @click="setStartPeriodType(u)">
                          {{ getPeriodTypeLabel(u, true) }}
                        </button>
                        <button v-if="u.pattern && selectedPeriodType===u" 
                                class="btn btn-sm ms-auto" 
                                :class="{'text-light':selectedPeriodType===u}" 
                                @click="u.edit=!u.edit" 
                                :disabled="paramError">
                          <i class="bi bi-pencil"></i>
                        </button>
                      </template>
                    </li>
                  </ul>
                </div>
              </div> 
              
             <!-- Right Column: Calendar Views -->
             <div class="col-lg-8  mt-2 mt-lg-0">
                <div class="row g-3">
                 

                  <!-- Dynamic End Period Controls -->
                  <div v-if="hasEndDynamicPeriod" class="col-12">
                    <div class="d-flex align-items-center gap-2">
                      <template v-if="selectedEndPeriodType">
                        <!-- Edit Mode for End Period -->
                        <div v-if="selectedEndPeriodType.edit" class="d-flex flex-column">
                          <div class="d-flex align-items-center">
                            <template v-for="(v,i) in selectedEndPeriodType.pieces" :key="i">
                              <div v-if="v==='static'">{{ selectedEndPeriodType.params[i] }}</div>
                              <input v-else-if="v==='num'" 
                                     type="number" 
                                     class="form-control mx-2 px-1"  
                                     v-model.number="selectedEndPeriodType.params[i]" 
                                     @change="setEndPeriodType(selectedEndPeriodType)"
                                     @keyup.enter="selectedEndPeriodType.edit = false">
                              <input v-else-if="v==='str'" 
                                     type="text" 
                                     class="form-control mx-2"  
                                     v-model="selectedEndPeriodType.params[i]"  
                                     @change="setEndPeriodType(selectedEndPeriodType)"
                                      @keyup.enter="selectedEndPeriodType.edit = false">
                            </template>
                          </div>
                          <div v-if="paramError" class="fs-sm text-danger">{{ paramError }}</div>
                        </div>

                        <!-- Normal End Period Display with Vue-controlled dropdown -->
                        <template v-else>
                          <div class="dropdown" ref="endPeriodDropdown">
                            <button class="btn btn-sm btn-outline-primary dropdown-toggle" 
                                    type="button"
                                    @click="toggleEndPeriodDropdown"
                                    :aria-expanded="showEndPeriodDropdown">
                              {{ getPeriodTypeLabel(selectedEndPeriodType, true) }}
                            </button>
                            
                            <ul class="dropdown-menu" :class="{ show: showEndPeriodDropdown }">
                              <li v-for="(u,i) in endPeriodTypes" :key="i">
                                <a class="dropdown-item" 
                                   href="#" 
                                   @click.prevent="selectEndPeriodType(u)">
                                  {{ getPeriodTypeLabel(u, true) }}
                                </a>
                              </li>
                            </ul>
                          </div>
                          
                          <button v-if="selectedEndPeriodType.pattern" 
                                  class="btn btn-sm btn-outline-secondary"  
                                  @click="selectedEndPeriodType.edit = true" 
                                  :disabled="paramError">
                            <i class="bi bi-pencil"></i>
                          </button>
                          
                          <button class="btn btn-sm btn-outline-danger" 
                                  @click="removeEndPeriodType()" 
                                  title="Remove">
                            <i class="bi bi-x-circle"></i>
                          </button>
                        </template>
                      </template>

                    </div>
                  </div>

                  <!-- Add Dynamic End Link -->
                  <div v-else-if="hasStartDynamicPeriod && !hasEndDynamicPeriod && hasEndPeriodTypes" 
                       class="col-12">
                    <button class="btn btn-sm btn-outline-primary" @click="setDefaultDynamicEndPeriodType()">
                      <i class="bi bi-plus me-1"></i>Add dynamic end
                    </button>
                  </div>

                   <!-- Start Date Calendar -->
                  <div class="col-lg-6 d-flex justify-content-center">
                    <div class="calendar-container" 
                      :class="{ 'calendar-disabled': !isCustomDate }"
                      >
                    
                    <DatePicker ref="calendarStart" 
                                class="inline-block h-full" 
                                :is-required="true"
                                v-model="resolvedStartDate" 
                                :disabledDates="disabledStartDates">
                      <template #footer>
                        <div class="w-full">
                          <VcDatePicker v-model="resolvedStartDate" 
                                        :enabled="isCustomDate" 
                                        :maxValue="resolvedEndDate"/>
                        </div>
                      </template>
                    </DatePicker>
                    <!-- Blocking overlay -->
                    <transition name="fade">
                      <div v-if="!isCustomDate" class="calendar-blocker">
                        <!--<div class="blocker-message">
                          <i class="bi bi-lock"></i>
                          <span>Select custom date type</span>
                        </div>-->
                      </div>
                    </transition>
                    </div>
                  </div>

                  <!-- End Date Calendar -->
                  <div v-if="isDateRange" class="col-lg-6 d-flex justify-content-center">
                    <div class="calendar-container" 
                      :class="{ 'calendar-disabled': !isCustomDate }"
                      >
  
                    <DatePicker ref="calendarEnd" 
                                class="inline-block h-full" 
                                v-model="resolvedEndDate"  
                                :disabledDates="disabledEndDates">
                      <template #footer>
                        <div class="w-full">
                          <VcDatePicker v-model="resolvedEndDate" 
                                        :enabled="isCustomDate" 
                                        :minValue="resolvedStartDate"/>
                        </div>
                      </template>
                    </DatePicker>
                    <!-- Blocking overlay -->
                    <transition name="fade">
                      <div v-if="!isCustomDate" class="calendar-blocker">
                        <!--<div class="blocker-message">
                          <i class="bi bi-lock"></i>
                          <span>Select custom date type</span>
                        </div>-->
                      </div>
                    </transition>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer">
            <div v-if="rangeError" class="d-flex align-items-center fs-sm text-danger">{{ rangeError }}</div>
           
            <button type="button" class="btn btn-secondary" @click="cancelAndClose">
              Cancel
            </button>
             <button type="button" class="btn btn-primary" :disabled="isInvalidOutput" @click="applyAndClose">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {VcDateUtils} from '/src/visualcrossing-core/vcdateutils';
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import '/src/website-core/VcUtils';
import VcDatePicker from './VcDatePicker.vue'
import VcTooltip from './VcTooltip.vue';
export default {
  name: 'DateRangeChooser',
  props: {
    sessionId: String,
    sessionState: Object,
    queryDefinition: Object,
    queryData: Object,
    linkClass: {
      type: [String, Array, Object],
      default: ''
    }
  },
  emits: ['change:daterange'],
  data() {
    return {
      // Add modal visibility state
      showModal: false,
      // Add dropdown state
      showEndPeriodDropdown: false,
      // Your existing data properties
      periodTypes: [
        {id:"singledate",label:"Single date", rangetype:"single"},
        {id:"daterange",label:"Date Range", rangetype:"range"},
        {id:"default",label:"15 day forecast", rangetype:"range", dynamic:true},
        {id:"today",label:"Today", rangetype:"single", dynamic:true},
        {id:"yesterday",label:"Yesterday", rangetype:"single", dynamic:true},
        {id:"tomorrow",label:"Tomorrow", rangetype:"single", dynamic:true},
        {id:"yeartodate",label:"Year to date", rangetype:"range", dynamic:true},
        {id:"monthtodate",label:"Month to date", rangetype:"range", dynamic:true},
        {id:"lastNdays",label:"Last ([0-9]+) days", rangetype:"range", pattern:"(last)([0-9]+)(days)", dynamic:true, params:["last",7,"days"], pieces:["static", "num","static"]},
        {id:"nextNdays",label:"Next ([0-9]+) days", rangetype:"range", pattern:"(next)([0-9]+)(days)", dynamic:true, params:["next",7,"days"], pieces:["static", "num","static"]},
        {id:"lastNhours",label:"Last ([0-9]+) hours", rangetype:"range", pattern:"(last)([0-9]+)(hours)", dynamic:true, params:["last",24,"hours"], pieces:["static", "num","static"]},
        {id:"nextNhours",label:"Next ([0-9]+) hours", rangetype:"range", pattern:"(next)([0-9]+)(hours)", dynamic:true, params:["next",24,"hours"], pieces:["static", "num","static"]},
      ],
      queryDefn: null, 
      startPeriod: null,
      endPeriod: null,
      selectedPeriodType: null,
      selectedEndPeriodType: null,
      paramError: null,
      showEndPeriodDropdown: false, // Control variable for dropdown
      showStartPeriodDropdown: false
    }
  },
  methods: {
    // New modal control methods
    closeModal() {
      this.showModal = false;
      // Optionally cancel changes when closing via backdrop/X
      this.cancelChanges();
    },
    
    applyAndClose() {
      this.applyDateRange();
      this.showModal = false;
    },
    
    cancelAndClose() {
      this.cancelChanges();
      this.showModal = false;
    },


    toggleStartPeriodDropdown() {
      this.showStartPeriodDropdown = !this.showStartPeriodDropdown;
    },

    selectStartPeriodType(periodType) {
      this.setStartPeriodType(periodType);
      this.showStartPeriodDropdown = false;
    },

    closeDropdowns() {
      this.showEndPeriodDropdown = false;
      this.showStartPeriodDropdown = false;
    },

      // Dropdown control methods
    toggleEndPeriodDropdown() {
      this.showEndPeriodDropdown = !this.showEndPeriodDropdown;
    },
    
    selectEndPeriodType(periodType) {
      this.setEndPeriodType(periodType);
      this.showEndPeriodDropdown = false;
    },
    
   
    
    handleClickOutside(event) {
      // Close dropdown when clicking outside
      if (this.$refs.endPeriodDropdown && !this.$refs.endPeriodDropdown.contains(event.target)) {
        this.closeDropdowns();
      }
    },
    // Handle ESC key
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.showModal) {
        this.closeModal();
      }
    },

    cancelChanges() {
      let me = this;
      me.setPeriodFromQueryDefn(me.queryDefinition && JSON.parse(JSON.stringify(me.queryDefinition)));
    },
    
    applyDateRange() {
      let me = this;
      me.$emit("change:daterange", me.startPeriod, me.endPeriod);
    },
    getPeriodTypeLabel(type, preventResolution) {
      let me=this;
      if (!preventResolution && !type.dynamic) {
        return me.rangeSummary;
      }

      if (!type.pattern) return type.label;
      
      return type.params.join(" ");
    },
    findPeriodTypeById(id) {
      let me=this;
      if (!id) return null;
      return me.periodTypes.reduce((a,d)=> {
        if (a) return a;

        if (d.id===id) return d;

        if (d.pattern) { 
          let match=id.match(d.pattern);
          if (match && match.length) {
            match.forEach( (e,i)=> {
            if (i===0) return;
            d.params[i-1]=e;
          });
          return d;
        }
      }
        return null;
      }, null);

    },
    identifyPeriodType(startPeriod,endPeriod) {
      let me=this;
      if (!startPeriod && !endPeriod) {
        me.selectedPeriodType= me.findPeriodTypeById("default");
        return;
      }
      const fromIsDate=me.safeDateFromIso(startPeriod);
      const toIsDate=me.safeDateFromIso(endPeriod);
      if (fromIsDate && !toIsDate) {
        me.selectedPeriodType= me.findPeriodTypeById("singledate");
        return;
      }
      if (fromIsDate && toIsDate) {
        me.selectedPeriodType=me.findPeriodTypeById("daterange");
        return;
      }
      let fixedFromItem=me.findPeriodTypeById(startPeriod);
      if (fixedFromItem) {
        me.selectedPeriodType=fixedFromItem;
        let fixedEndItem=me.findPeriodTypeById(endPeriod);
        if (fixedEndItem) {
          me.selectedEndPeriodType=fixedEndItem;
        }
        return;
      }
      
      me.periodTypes[1];

    },

   
    

    setStartPeriodType(newType) {
      let me=this;
      me.paramError=null;
      if (newType.id==='default') {
        //15 day forecast
        me.startPeriod=null;
        me.endPeriod=null;
      } else if (newType.params) {
        let period=newType.params.join("");
        let periodMatch=period.match(newType.pattern);
        if (!periodMatch) {
          me.paramError="Invalid parameter";
          return;
        }
        me.startPeriod=period;
        me.endPeriod=null;
        me.selectedEndPeriodType=null;
      } else if (newType.dynamic) {
        me.startPeriod=newType.id;
        me.endPeriod=null;
        me.selectedEndPeriodType=null;
      } else if (!newType.dynamic) {
        //direct assignment causes the vaues to be recalculated
        let startPeriod=me.resolvedStartDate && VcDateUtils.formatDateIso(me.resolvedStartDate);
        let endPeriod=me.resolvedEndDate && VcDateUtils.formatDateIso(me.resolvedEndDate);
        me.startPeriod=startPeriod;
        me.endPeriod=newType.id==="daterange"?endPeriod:null;
        me.selectedEndPeriodType=null;
      }
      if ((newType.id==="singledate" || newType.id==="daterange")  && !me.startPeriod) {
        me.startPeriod=VcDateUtils.formatDateIso(new Date());
       
      }

      if (newType.id==="daterange" && !me.endPeriod) {
        me.endPeriod=me.startPeriod;
      }

      me.selectedPeriodType=newType;
    },
     removeStartPeriodType() {
      let me=this;
      me.selectedStartPeriodType=null;
      me.startPeriod=null;
    },
    setDefaultDynamicEndPeriodType() {
      let me=this;
      if (me.hasEndPeriodTypes) {
        let newType=me.endPeriodTypes[0];
        me.setEndPeriodType(newType);
      } 
    },
    setDefaultDynamicStartPeriodType() {
      let me=this;
      if (me.dynamicPeriodTypes && me.dynamicPeriodTypes.length) {
        let newType=me.dynamicPeriodTypes[0];
        me.setStartPeriodType(newType);
      } 
    },
    setEndPeriodType(newType) {
      let me=this;

      

      if (!newType) return;
      if (!newType.dynamic) {
        me.paramError="End period must be dynamic";
        return;
      }
      if (newType.params) {
        let period=newType.params.join("");
        let periodMatch=period.match(newType.pattern);
        if (!periodMatch) {
          me.paramError="Invalid parameter";
          return;
        }
      
        me.endPeriod=period;
      } else if (newType.dynamic) {
        me.endPeriod=newType.id;
      }


      me.selectedEndPeriodType=newType;
      
    },
    removeEndPeriodType() {
      let me=this;
      me.selectedEndPeriodType=null;
      me.endPeriod=null;
    },
   
    setPeriodFromQueryDefn(queryDefn) {
      let me=this;
      if (!queryDefn) {
        me.startPeriod=null;
        me.endPeriod=null;
        return;
      }

      me.startPeriod=queryDefn.startdate;
      me.endPeriod=queryDefn.enddate;
      me.identifyPeriodType(me.startPeriod, me.endPeriod)
      //me.selectedPeriodType=me.periodTypes[1];
    },
    safeDateFromIso(value) {
      if (!value) return null;
      let date=VcDateUtils.dateFromIsoLocal(value);
      return date && VcUtils.isValidDate(date) && date;
    },

   
  },
  computed:{
    disabledStartDates() {
      let me = this;
      const minDate = new Date('1970-01-01');
      
      if (me.isSingleDate && !me.hasStartDynamicPeriod) {
        return [{
          end: minDate  // Disable all dates before 1970-01-01
        }];
      }
      
      if (!me.isCustomDate) {
        return [
          {
            start: me.resolvedStartDate && VcDateUtils.addDays(me.resolvedStartDate, 1),
          }, 
          {
            end: me.resolvedStartDate ? VcDateUtils.addDays(me.resolvedStartDate, -1) : minDate,
          }
        ];
      }

      return [
        {
          start: me.resolvedEndDate,
        },
        {
          end: minDate  // Always disable dates before 1970-01-01
        }
      ];
    },

    disabledEndDates() {
      let me = this;
      const minDate = new Date('1970-01-01');
      
      if (me.isSingleDate && !me.hasStartDynamicPeriod) {
        return [{
          end: minDate  // Disable all dates before 1970-01-01
        }];
      }
      
      if (!me.isCustomDate || !me.isCustomEndDate) {
        return [
          {
            start: me.resolvedEndDate && VcDateUtils.addDays(me.resolvedEndDate, 1),
          }, 
          {
            end: me.resolvedEndDate ? VcDateUtils.addDays(me.resolvedEndDate, -1) : minDate,
          }
        ];
      }
      
      return [
        {
          end: me.resolvedStartDate || minDate,  // Use minDate if no start date
        }
      ];
    },
    isInvalidOutput() {
      let me=this;
      if (me.paramError) return true;
      let startDate=me.resolvedStartDate;
      let endDate=me.resolvedEndDate;

      if (!startDate || (startDate && endDate && endDate.getTime()<startDate.getTime()))  {
        //me.paramError="End date cannot be before the start date";
        return true;
      }
      return false;
    },
    rangeError() {
      let me=this;
      if (me.paramError) return "Parameter error";
      let startDate=me.resolvedStartDate;
      let endDate=me.resolvedEndDate;

      if (!startDate || (startDate && endDate && endDate.getTime()<startDate.getTime()))  {
        return "End date cannot be before the start date";
      }

      return null;
    },
    isEditing() {
      let me=this;
      return me.periodTypes.reduce((a,d)=>{
        return a || d.edit;
      },false)
    },
    hasStartDynamicPeriod() {
      let me=this;
      return me.selectedPeriodType && me.selectedPeriodType.dynamic;
    },
    hasEndDynamicPeriod() {
      let me=this;
      return me.selectedEndPeriodType && me.selectedEndPeriodType.dynamic;
    },
    hasEndPeriodTypes() {
      let me=this;
      return me.endPeriodTypes && me.endPeriodTypes.length;
    },
    customPeriodTypes() {
      let me=this;
      return me.periodTypes.filter(( d)=>{
        return !d.dynamic;
      });
    },
    dynamicPeriodTypes() {
      let me=this;
      return me.periodTypes.filter(( d)=>{
        return d.dynamic;
      });
    },
    endPeriodTypes() {
      let me=this;
      let endDate;
      if (me.isCustomDate) {
        return [];
        } else {
          const dynamicRange=VcDateUtils.resolveDynamicDate(me.startPeriod);
          endDate= dynamicRange && (dynamicRange[1] || dynamicRange[0]);
        }

      
      if (!endDate) return [];

      return me.periodTypes.reduce((a, d)=>{
        if (!d.dynamic) return a;
        let id=d.id;
        if (d.params) {
          id=d.params.join("");
        }
        const dynamicRange=VcDateUtils.resolveDynamicDate(id);
        const periodEndDate= dynamicRange && (dynamicRange[1] || dynamicRange[0]);

        if (periodEndDate && periodEndDate.getTime()>=endDate.getTime()) {
          a.push(JSON.parse(JSON.stringify(d)));
        }

        return a;

      }, []);

    },
    isSingleDate() {
      let me=this;
      return !me.selectedPeriodType || me.selectedPeriodType.rangetype==='single';
    },
    isDateRange() {
      let me=this;
      return me.selectedPeriodType && (me.selectedPeriodType.rangetype==='range' || me.selectedEndPeriodType);
    },
    title() {
     let me=this;

     if (me.selectedPeriodType && me.selectedEndPeriodType) {
      return `${me.getPeriodTypeLabel(me.selectedPeriodType)} - ${me.getPeriodTypeLabel(me.selectedEndPeriodType)}`;
     } else  if (me.selectedPeriodType) {
      return `${me.getPeriodTypeLabel(me.selectedPeriodType)}`;
     }

     if (!me.startPeriod) {
      return "15 day forecast";
     } else if (me.endPeriod) {
      return `${me.startPeriod} - ${me.endPeriod}`;
     } else {
      return me.startPeriod;
     }
    //return `${me.startPeriod} - ${me.endPeriod} (${resolvedStartText} - ${resolvedEndText})`;
    },
    rangeSummary() {
      let me=this;
      let resolvedStartText=me.resolvedStartDate && VcDateUtils.formatDateIso(me.resolvedStartDate);

      if (me.isSingleDate) {
         return `${resolvedStartText}`;
      }
      let resolvedEndText=me.resolvedEndDate && VcDateUtils.formatDateIso(me.resolvedEndDate);
      return `${resolvedStartText} - ${resolvedEndText}`;
    },
    isCustomDate() {
      let me=this;
      let fromIsDate=me.safeDateFromIso(me.startPeriod);
      let toIsDate=me.safeDateFromIso(me.endPeriod);
      //const toIsDate=VcUtils.isValidDate(startPeriod);
      return fromIsDate?true:false;
      //VcDateUtils.dateFromIsoLocal(me.startPeriod);
      //return me.selectedPeriodType && (me.selectedPeriodType.id==='singledate' || me.selectedPeriodType.id==='daterange');
    },
    isCustomEndDate() {
      let me=this;
     // let fromIsDate=me.safeDateFromIso(me.startPeriod);
      let toIsDate=me.safeDateFromIso(me.endPeriod);
      //const toIsDate=VcUtils.isValidDate(startPeriod);
      return toIsDate?true:false;
      //VcDateUtils.dateFromIsoLocal(me.startPeriod);
      //return me.selectedPeriodType && (me.selectedPeriodType.id==='singledate' || me.selectedPeriodType.id==='daterange');
    },
    resolvedDateRange: {
      get: function () {
        let me=this;
        return {
          start: me.resolvedStartDate,
          end: me.resolvedEndDate
        }
      },
      set: function (newValue) {
        var me=this;
        
        if (!me.isCustomDate) return;
       
        
        me.startPeriod=newValue.start && VcDateUtils.formatDateIso(newValue.start);
        me.endPeriod=newValue.end && VcDateUtils.formatDateIso(newValue.end);
        //me.$emit("change:daterange", me.queryDefn);
      }
    },
    resolvedStartDate: {
      get: function () {
        var me = this;
        let date;
        
        if (me.isCustomDate && me.startPeriod) {
          date = VcDateUtils.dateFromIsoLocal(me.startPeriod);
        } else {
          const dynamicRange = VcDateUtils.resolveDynamicDate(me.startPeriod);
          date = dynamicRange && dynamicRange[0];
        }
        
        // Ensure date is not before 1970-01-01
        if (date && date < new Date('1970-01-01')) {
          return new Date('1970-01-01');
        }
        
        return date;

       
      },
      set: function (newValue) {
        var me=this;

        if (!newValue) return;
        if (!me.isCustomDate) return;
        // Validate the date is not before 1970-01-01
        const minDate = new Date('1970-01-01');
        if (newValue < minDate) {
          newValue = minDate;
        }
        //let currentStartDate=me.safeDateFromIso(me.startPeriod);
       // me.endPeriod=null;
        /*
        let currentEndDate=me.safeDateFromIso(me.endPeriod);

       
        if (currentEndDate && currentEndDate.getTime()<newValue.getTime()) {
          me.startPeriod=VcDateUtils.formatDateIso(newValue);
        }
          */
        me.startPeriod=VcDateUtils.formatDateIso(newValue);
        console.log("Set start period to ", me.startPeriod);
        //me.$emit("change:daterange", me.queryDefn);
      }
    },
    resolvedEndDate: {
      get: function () {
        var me = this;
        let date;
        
        if (me.isCustomDate) {
          date = me.endPeriod && VcDateUtils.dateFromIsoLocal(me.endPeriod);
        } else {
          const dynamicRange = VcDateUtils.resolveDynamicDate(me.endPeriod || me.startPeriod);
          date = dynamicRange && (dynamicRange[1] || dynamicRange[0]);
        }
        
        // Ensure date is not before 1970-01-01
        if (date && date < new Date('1970-01-01')) {
          return new Date('1970-01-01');
        }
        
        return date;

      },
      set: function (newValue) {
        var me = this;
        if (!me.isCustomEndDate) return;
        
        // Validate the date is not before 1970-01-01
        const minDate = new Date('1970-01-01');
        if (newValue < minDate) {
          newValue = minDate;
        }
        
        let currentStartDate = me.safeDateFromIso(me.startPeriod);
        
        if (currentStartDate && currentStartDate.getTime() > newValue.getTime()) {
          me.startPeriod = VcDateUtils.formatDateIso(newValue);
        }
        
        me.endPeriod = VcDateUtils.formatDateIso(newValue);
      }
    },
     
    
  },
  watch:{
    resolvedStartDate:{
      handler:function(newValue,oldValue) {
        let me=this;
        if (newValue && me.$refs.calendarStart) me.$refs.calendarStart.move(newValue);
      },
    },
    resolvedEndDate:{
      handler:function(newValue,oldValue) {
        let me=this;
        if (newValue && me.$refs.calendarEnd) me.$refs.calendarEnd.move(newValue);
      },
    },
    queryDefinition:{
        handler:function(newValue,oldValue) {
          var me=this;
          me.setPeriodFromQueryDefn(newValue);
          //me.queryDefn=newValue && JSON.parse(JSON.stringify(newValue));
        },
        deep: true
    },
  },
  mounted() {
    var me=this;
    me.setPeriodFromQueryDefn(me.queryDefinition && JSON.parse(JSON.stringify(me.queryDefinition)));
    //me.queryDefn=me.queryDefinition && JSON.parse(JSON.stringify(me.queryDefinition));
  
     
  },
  updated() {
    var me=this;
  
  },
  components: {
    VcTooltip,
    DatePicker,
    VcDatePicker
   // CommonModals
  }
}
</script>

<style scoped>
 .calendar-override :deep(.vc-highlight) {
  background-color: #00628b !important;
}
.calendar-override :deep(.vc-nav-item) {
  color: #000000 !important;
}
.calendar-override :deep(.vc-nav-item.is-active) {
  color: #ffffff !important;
}

/* Modal styles to ensure proper display without Bootstrap JS */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1055;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
}

.modal.show {
  display: block !important;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1050;
  width: 100vw;
  height: 100vh;
  background-color: #000;
}

.modal-backdrop.show {
  opacity: 0.5;
}

/* Ensure modal content is above backdrop */
.modal-dialog {
  position: relative;
  z-index: 1060;
}

/* Prevent body scroll when modal is open */
body.modal-open {
  overflow: hidden;
}
.calendar-container {
  position: relative;
  display: inline-block;
}

.calendar-disabled {
  filter: contrast(0.7);
}

.calendar-blocker {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 249, 250, 0.1);
  z-index: 100;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  /*backdrop-filter: blur(2px);*/
}

.blocker-message {
  background: white;
  padding: 8px 16px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #6c757d;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
